/**
 * OAuth2DemoPanel — C035 OAuth2/OIDC 브릿지 라이브 데모
 * 계약: docs/common-specs/eraf-security/C035-oauth2-oidc.md §Δ.3
 * 서버간 Resource Owner Password grant 로 Keycloak(OIDC RS256) 에서 id_token 을 받아
 * OidcToErafTokenBridge 로 eraf HS384 TokenPair 로 교환한 뒤 두 토큰 클레임을 비교한다.
 * ★ 브라우저 리다이렉트 로그인 플로우가 아니다 — 내부망 IdP 는 브라우저에서 도달 불가하므로
 * biz-api 가 서버간으로 왕복을 대신 수행하고 그 결과(두 클레임셋 + 일치 배지)를 시각화한다.
 * 5상태: loading(status skeleton) / empty(브리지 실행 전) / error(status·bridge 실패) /
 *        default(status + 클레임 대비 카드 + 배지) / dialog(N/A — 인라인, disabled 안내로 대체)
 */
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useOAuth2Status, useOAuth2Bridge } from '../hooks/useOAuth2Demo';
import { oauth2FormSchema, type OAuth2FormValues } from '../schema/showcaseSchema';
import type { ApiError } from '../types/showcase';

export function OAuth2DemoPanel() {
  const { t } = useTranslation();
  const statusQuery = useOAuth2Status();
  const bridgeMutation = useOAuth2Bridge();

  const [bridgeError, setBridgeError] = useState<ApiError | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<OAuth2FormValues>({
    resolver: zodResolver(oauth2FormSchema),
    defaultValues: { username: '', password: '' },
  });

  const status = statusQuery.data;
  const isStatusLoading = statusQuery.isLoading;
  const isStatusError = statusQuery.isError;
  const disabled = !status?.enabled || !status?.demoUserConfigured;

  function onSubmit(values: OAuth2FormValues) {
    setBridgeError(null);
    bridgeMutation.mutate(
      {
        username: values.username ? values.username : undefined,
        password: values.password ? values.password : undefined,
      },
      { onError: (err) => setBridgeError(err) }
    );
  }

  return (
    <section aria-labelledby="oauth2-panel-title">
      <h2
        id="oauth2-panel-title"
        style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          marginBottom: 'var(--spacing-2)',
          marginTop: 0,
        }}
      >
        {t('showcase.oauth2.title')}
      </h2>
      <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--spacing-6)' }}>
        {t('showcase.oauth2.subtitle')}
      </p>

      {/* status — loading skeleton */}
      {isStatusLoading && (
        <div aria-busy="true" aria-label={t('showcase.oauth2.status.loadingAriaLabel')} style={cardStyle}>
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              style={{
                height: 16,
                width: `${35 + ((i * 15) % 40)}%`,
                borderRadius: 'var(--radius-sm)',
                background: 'var(--color-surface-elevated)',
                animation: 'pulse 1.5s ease-in-out infinite',
                marginBottom: 'var(--spacing-2)',
              }}
            />
          ))}
        </div>
      )}

      {/* status — error */}
      {isStatusError && (
        <div role="alert" style={{ ...cardStyle, border: '1px solid var(--color-danger)' }}>
          <p style={{ margin: 0, color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)' }}>
            {(statusQuery.error as ApiError)?.message ?? t('showcase.oauth2.status.loadFailed')}
          </p>
          <button type="button" onClick={() => void statusQuery.refetch()} style={secondaryButtonStyle}>
            {t('showcase.common.retry')}
          </button>
        </div>
      )}

      {/* status — default */}
      {!isStatusLoading && !isStatusError && status && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
          <div style={cardStyle}>
            <h3 style={sectionTitleStyle}>{t('showcase.oauth2.status.title')}</h3>
            <div style={infoGridStyle}>
              <InfoField label={t('showcase.oauth2.status.enabled')}>
                <span style={{ color: status.enabled ? 'var(--color-status-success)' : 'var(--color-status-error)', fontWeight: 700 }}>
                  {status.enabled ? t('showcase.oauth2.status.enabledYes') : t('showcase.oauth2.status.enabledNo')}
                </span>
              </InfoField>
              <InfoField label={t('showcase.oauth2.status.issuerUri')}>
                <code style={monoStyle}>{status.issuerUri ?? t('showcase.oauth2.status.issuerUriNone')}</code>
              </InfoField>
              <InfoField label={t('showcase.oauth2.status.clientId')}>
                <code style={monoStyle}>{status.clientId ?? t('showcase.oauth2.status.clientIdNone')}</code>
              </InfoField>
              <InfoField label={t('showcase.oauth2.status.mode')}>
                <code style={monoStyle}>{status.mode}</code>
              </InfoField>
            </div>

            {/* note — 브라우저 리다이렉트 불가 안내 (항상 노출) */}
            <p style={{ marginTop: 'var(--spacing-3)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
              {status.note}
            </p>

            {disabled && (
              <p style={{ marginTop: 'var(--spacing-3)', fontSize: 'var(--font-size-xs)', color: 'var(--color-warning)' }}>
                {t('showcase.oauth2.disabledNotice')}
              </p>
            )}
          </div>

          {/* bridge form */}
          <div style={cardStyle}>
            <h3 style={sectionTitleStyle}>{t('showcase.oauth2.bridge.title')}</h3>
            <form
              onSubmit={handleSubmit(onSubmit)}
              noValidate
              style={{ display: 'flex', gap: 'var(--spacing-3)', flexWrap: 'wrap', alignItems: 'flex-start' }}
            >
              <div style={{ flex: '1 1 200px' }}>
                <label htmlFor="oauth2-username" style={labelStyle}>
                  {t('showcase.oauth2.bridge.usernameLabel')}
                </label>
                <input
                  id="oauth2-username"
                  type="text"
                  placeholder={t('showcase.oauth2.bridge.usernamePlaceholder')}
                  aria-invalid={!!errors.username}
                  aria-describedby={errors.username ? 'oauth2-username-error' : undefined}
                  disabled={disabled}
                  {...register('username')}
                  style={inputStyle(disabled)}
                />
                {errors.username && (
                  <p id="oauth2-username-error" role="alert" style={errorTextStyle}>
                    {errors.username.message}
                  </p>
                )}
              </div>
              <div style={{ flex: '1 1 200px' }}>
                <label htmlFor="oauth2-password" style={labelStyle}>
                  {t('showcase.oauth2.bridge.passwordLabel')}
                </label>
                <input
                  id="oauth2-password"
                  type="password"
                  placeholder={t('showcase.oauth2.bridge.passwordPlaceholder')}
                  aria-invalid={!!errors.password}
                  aria-describedby={errors.password ? 'oauth2-password-error' : undefined}
                  disabled={disabled}
                  {...register('password')}
                  style={inputStyle(disabled)}
                />
                {errors.password && (
                  <p id="oauth2-password-error" role="alert" style={errorTextStyle}>
                    {errors.password.message}
                  </p>
                )}
              </div>
              <button
                type="submit"
                disabled={disabled || bridgeMutation.isPending}
                aria-busy={bridgeMutation.isPending}
                style={{
                  ...primaryButtonStyle,
                  alignSelf: 'flex-end',
                  opacity: disabled || bridgeMutation.isPending ? 0.6 : 1,
                  cursor: disabled || bridgeMutation.isPending ? 'not-allowed' : 'pointer',
                }}
              >
                {bridgeMutation.isPending ? t('showcase.oauth2.bridge.sending') : t('showcase.oauth2.bridge.button')}
              </button>
            </form>

            {/* bridge — error */}
            {bridgeError && !bridgeMutation.isPending && (
              <div role="alert" style={{ marginTop: 'var(--spacing-3)', ...errorBoxStyle }}>
                {bridgeError.message ?? t('showcase.oauth2.bridge.error')}
              </div>
            )}

            {/* bridge — empty (실행 전) */}
            {!bridgeMutation.data && !bridgeError && !bridgeMutation.isPending && (
              <p style={{ marginTop: 'var(--spacing-3)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                {t('showcase.oauth2.bridge.empty')}
              </p>
            )}

            {/* bridge — default (결과: badges + 2-column claim comparison) */}
            {bridgeMutation.data && !bridgeMutation.isPending && (
              <div style={{ marginTop: 'var(--spacing-4)' }}>
                <div
                  role="group"
                  aria-label={t('showcase.oauth2.bridge.badgesAriaLabel')}
                  style={{ display: 'flex', gap: 'var(--spacing-2)', flexWrap: 'wrap', marginBottom: 'var(--spacing-4)' }}
                >
                  <Badge ok={bridgeMutation.data.bridged} label={t('showcase.oauth2.bridge.badge.bridged')} />
                  <Badge ok={bridgeMutation.data.subjectMatch} label={t('showcase.oauth2.bridge.badge.subjectMatch')} />
                  <Badge ok={bridgeMutation.data.rolesMatch} label={t('showcase.oauth2.bridge.badge.rolesMatch')} />
                  <AlgBadge alg={bridgeMutation.data.oidc.alg} label={t('showcase.oauth2.bridge.badge.oidcAlg')} />
                  <AlgBadge alg={bridgeMutation.data.eraf.alg} label={t('showcase.oauth2.bridge.badge.erafAlg')} />
                </div>

                <div
                  role="region"
                  aria-label={t('showcase.oauth2.bridge.comparisonAriaLabel')}
                  style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 'var(--spacing-4)' }}
                >
                  <ClaimCard
                    title={t('showcase.oauth2.bridge.oidcTitle')}
                    rows={[
                      [t('showcase.oauth2.bridge.claim.iss'), bridgeMutation.data.oidc.iss],
                      [t('showcase.oauth2.bridge.claim.sub'), bridgeMutation.data.oidc.sub],
                      [t('showcase.oauth2.bridge.claim.email'), bridgeMutation.data.oidc.email],
                      [t('showcase.oauth2.bridge.claim.roles'), bridgeMutation.data.oidc.roles.join(', ')],
                      [t('showcase.oauth2.bridge.claim.exp'), String(bridgeMutation.data.oidc.exp)],
                      [t('showcase.oauth2.bridge.claim.alg'), bridgeMutation.data.oidc.alg],
                    ]}
                  />
                  <ClaimCard
                    title={t('showcase.oauth2.bridge.erafTitle')}
                    rows={[
                      [t('showcase.oauth2.bridge.claim.sub'), bridgeMutation.data.eraf.sub],
                      [t('showcase.oauth2.bridge.claim.userId'), bridgeMutation.data.eraf.userId],
                      [t('showcase.oauth2.bridge.claim.roles'), bridgeMutation.data.eraf.roles.join(', ')],
                      [t('showcase.oauth2.bridge.claim.exp'), String(bridgeMutation.data.eraf.exp)],
                      [t('showcase.oauth2.bridge.claim.alg'), bridgeMutation.data.eraf.alg],
                    ]}
                  />
                </div>

                <p style={{ marginTop: 'var(--spacing-3)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                  {t('showcase.oauth2.bridge.traceId')} <code style={monoStyle}>{bridgeMutation.data.traceId}</code>
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Learning */}
      <div
        style={{
          marginTop: 'var(--spacing-4)',
          padding: 'var(--spacing-3)',
          background: 'var(--color-surface-elevated)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-secondary)',
        }}
      >
        <span aria-hidden="true" style={{ marginRight: 'var(--spacing-2)' }}>📚</span>
        <strong>{t('showcase.panel.learning')}</strong> {t('showcase.oauth2.learning')}
      </div>
    </section>
  );
}

function Badge({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--spacing-1)',
        padding: '2px var(--spacing-2)',
        borderRadius: 'var(--radius-sm)',
        fontSize: 'var(--font-size-xs)',
        fontWeight: 700,
        color: ok ? 'var(--color-status-success)' : 'var(--color-status-error)',
        background: 'var(--color-surface-elevated)',
        border: `1px solid ${ok ? 'var(--color-status-success)' : 'var(--color-status-error)'}`,
      }}
    >
      {label}: {ok ? 'OK' : 'MISMATCH'}
    </span>
  );
}

function AlgBadge({ alg, label }: { alg: string; label: string }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--spacing-1)',
        padding: '2px var(--spacing-2)',
        borderRadius: 'var(--radius-sm)',
        fontSize: 'var(--font-size-xs)',
        fontWeight: 700,
        fontFamily: 'var(--font-family-mono)',
        color: 'var(--color-text-secondary)',
        background: 'var(--color-surface-elevated)',
        border: '1px solid var(--color-surface-border)',
      }}
    >
      {label}: {alg}
    </span>
  );
}

function ClaimCard({ title, rows }: { title: string; rows: [string, string][] }) {
  return (
    <div style={{ ...cardStyle, padding: 'var(--spacing-4)' }}>
      <h4 style={{ margin: '0 0 var(--spacing-3) 0', fontSize: 'var(--font-size-sm)', fontWeight: 700, color: 'var(--color-text-primary)' }}>
        {title}
      </h4>
      <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 'var(--spacing-1) var(--spacing-3)' }}>
        {rows.map(([k, v]) => (
          <Fragment key={k}>
            <dt style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontWeight: 600 }}>{k}</dt>
            <dd style={{ margin: 0, fontSize: 'var(--font-size-xs)', fontFamily: 'var(--font-family-mono)', color: 'var(--color-text-primary)', wordBreak: 'break-all' }}>
              {v}
            </dd>
          </Fragment>
        ))}
      </dl>
    </div>
  );
}

function InfoField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-1)' }}>{label}</p>
      <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)' }}>{children}</div>
    </div>
  );
}

function inputStyle(disabled: boolean): React.CSSProperties {
  return {
    width: '100%',
    padding: 'var(--spacing-3)',
    border: '1px solid var(--color-surface-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    fontFamily: 'var(--font-family-sans)',
    background: disabled ? 'var(--color-surface-muted)' : 'var(--color-surface-background)',
    color: 'var(--color-text-primary)',
    boxSizing: 'border-box',
  };
}

const cardStyle: React.CSSProperties = {
  padding: 'var(--spacing-5)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-surface-background)',
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-base)',
  fontWeight: 700,
  color: 'var(--color-text-primary)',
  marginBottom: 'var(--spacing-4)',
};

const infoGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
  gap: 'var(--spacing-4)',
};

const monoStyle: React.CSSProperties = {
  fontFamily: 'var(--font-family-mono)',
  fontSize: 'var(--font-size-sm)',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-secondary)',
  marginBottom: 'var(--spacing-1)',
};

const primaryButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: 'var(--spacing-2) var(--spacing-4)',
  background: 'var(--color-brand-primary)',
  color: 'var(--color-text-inverse)',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 600,
  fontFamily: 'var(--font-family-sans)',
};

const secondaryButtonStyle: React.CSSProperties = {
  padding: 'var(--spacing-1) var(--spacing-3)',
  background: 'var(--color-surface-elevated)',
  color: 'var(--color-text-primary)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-xs)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

const errorTextStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-danger)',
  marginTop: 'var(--spacing-1)',
};

const errorBoxStyle: React.CSSProperties = {
  padding: 'var(--spacing-3)',
  border: '1px solid var(--color-danger)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-surface-muted)',
  color: 'var(--color-danger)',
  fontSize: 'var(--font-size-sm)',
};
